/**
 * Validation result for prompt template variables
 */
export interface ValidationResult {
  isValid: boolean;
  invalidVariables: string[];
  errorMessage?: string;
}

/**
 * Validates template variables in a system prompt.
 * 
 * Rules:
 * - Double curly braces ({{ and }}) are treated as escaped formatting and ignored
 * - Single curly braces are treated as template variables
 * - Only {USER_DETAILS} is allowed as a valid template variable
 * - Any other variable in single braces is considered invalid
 * - Unmatched single braces ({ or }) will cause formatting errors
 * 
 * @param prompt - The system prompt text to validate
 * @returns ValidationResult with isValid flag, list of invalid variables, and error message
 */
export function validatePromptVariables(prompt: string): ValidationResult {
  if (!prompt) {
    return {
      isValid: true,
      invalidVariables: [],
    };
  }

  // Step 1: Check for unmatched single braces (will cause backend formatting errors)
  // Remove double braces first, then check for unmatched singles
  let workingText = prompt;
  
  // Temporarily replace double braces with placeholders
  workingText = workingText.replace(/\{\{/g, '__DOUBLE_OPEN__');
  workingText = workingText.replace(/\}\}/g, '__DOUBLE_CLOSE__');
  
  // Now check for unmatched single braces
  const lines = workingText.split('\n');
  const unmatchedBraces: Array<{line: number, char: string, position: number}> = [];
  
  lines.forEach((line, lineIndex) => {
    let openCount = 0;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '{') {
        openCount++;
      } else if (line[i] === '}') {
        openCount--;
        if (openCount < 0) {
          unmatchedBraces.push({
            line: lineIndex + 1,
            char: '}',
            position: i + 1
          });
          openCount = 0; // Reset to continue checking
        }
      }
    }
    // Check for unclosed braces at end of line
    if (openCount > 0) {
      const lastOpenIndex = line.lastIndexOf('{');
      unmatchedBraces.push({
        line: lineIndex + 1,
        char: '{',
        position: lastOpenIndex + 1
      });
    }
  });

  if (unmatchedBraces.length > 0) {
    const errorDetails = unmatchedBraces.map(b => 
      `Line ${b.line}, position ${b.position}: unmatched '${b.char}'`
    ).join('\n');
    
    return {
      isValid: false,
      invalidVariables: [],
      errorMessage: `Unmatched braces found (will cause formatting errors):\n${errorDetails}\n\nEach '{' must have a matching '}'. Use '{{' and '}}' for literal braces.`,
    };
  }

  // Step 2: Remove all double curly braces (escaped formatting)
  const withoutDoubleBraces = prompt.replace(/\{\{|\}\}/g, '');

  // Step 3: Extract all single curly brace patterns
  // Match {anything} where anything doesn't contain closing brace
  const variablePattern = /\{([^}]+)\}/g;
  const matches = Array.from(withoutDoubleBraces.matchAll(variablePattern));
  const variables = matches.map(m => m[1].trim());

  // Step 4: Check for invalid variables (anything other than USER_DETAILS)
  const invalidVars = variables.filter(v => v !== 'USER_DETAILS' && v !== '');

  // Step 5: Return validation result
  if (invalidVars.length === 0) {
    return {
      isValid: true,
      invalidVariables: [],
    };
  }

  return {
    isValid: false,
    invalidVariables: invalidVars,
    errorMessage: `Invalid template variables found: ${invalidVars.map(v => `{${v}}`).join(', ')}. Only {USER_DETAILS} is allowed.`,
  };
}
